import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Crown, Shield, TrendingUp, Ban, CheckCircle, FileText, Video, Trash2, Plus, Home, Activity } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProperties: 0,
    totalPosts: 0,
    profesional: 0,
    avanzado: 0,
    usuario: 0,
    banned: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadStats();
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const loadStats = async () => {
    try {
      const [usersRes, propsRes, postsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/community/posts`)
      ]);
      
      const usersList = usersRes.data.users || usersRes.data || [];
      const activeUsersCount = usersList.filter(u =>
        ['profesional', 'premium', 'usuario_avanzado', 'essential'].includes(u.subscription_plan) && !u.banned
      ).length;

      setStats({
        totalUsers: usersList.length,
        activeUsers: activeUsersCount,
        totalProperties: propsRes.data.length || 0,
        totalPosts: postsRes.data.length || 0,
        profesional: usersList.filter(u => u.subscription_plan === 'profesional' || u.subscription_plan === 'premium').length,
        avanzado: usersList.filter(u => u.subscription_plan === 'usuario_avanzado' || u.subscription_plan === 'essential').length,
        usuario: usersList.filter(u => !u.subscription_plan || u.subscription_plan === 'usuario' || u.subscription_plan === 'basic').length,
        banned: usersList.filter(u => u.banned).length
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Error al cargar estadísticas');
      setLoading(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white text-xl">Acceso denegado</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto pt-20 md:pt-8">
        <PageTitle>Panel Administrativo</PageTitle>
        
        {/* Stats Grid - Tiempo Real */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-xl border-4 border-blue-400 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <Users className="text-white" size={36} />
              <div className="bg-white/20 px-3 py-1 rounded-full text-xs text-white font-bold">TIEMPO REAL</div>
            </div>
            <div className="text-white/90 text-sm mb-1">Usuarios Totales</div>
            <div className="text-white text-4xl font-bold mb-1">{stats.totalUsers}</div>
            <div className="text-white/70 text-xs">Registrados en la plataforma</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-600 to-green-800 p-6 rounded-xl border-4 border-green-400 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <Activity className="text-white" size={36} />
              <div className="bg-white/20 px-3 py-1 rounded-full text-xs text-white font-bold">TIEMPO REAL</div>
            </div>
            <div className="text-white/90 text-sm mb-1">Usuarios Activos</div>
            <div className="text-white text-4xl font-bold mb-1">{stats.activeUsers}</div>
            <div className="text-white/70 text-xs">Con suscripción Avanzado/Profesional</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-6 rounded-xl border-4 border-purple-400 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <Home className="text-white" size={36} />
              <div className="bg-white/20 px-3 py-1 rounded-full text-xs text-white font-bold">TIEMPO REAL</div>
            </div>
            <div className="text-white/90 text-sm mb-1">Propiedades Publicadas</div>
            <div className="text-white text-4xl font-bold mb-1">{stats.totalProperties}</div>
            <div className="text-white/70 text-xs">Anuncios activos</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-600 to-orange-800 p-6 rounded-xl border-4 border-orange-400 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <FileText className="text-white" size={36} />
              <div className="bg-white/20 px-3 py-1 rounded-full text-xs text-white font-bold">TIEMPO REAL</div>
            </div>
            <div className="text-white/90 text-sm mb-1">Posts Comunidad</div>
            <div className="text-white text-4xl font-bold mb-1">{stats.totalPosts}</div>
            <div className="text-white/70 text-xs">Publicaciones totales</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/videos')}
            className="bg-gradient-to-r from-pink-600 to-red-600 p-6 rounded-xl border-4 border-pink-400 hover:scale-105 transition-all duration-300 shadow-xl group"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full">
                <Video size={32} className="text-white" />
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-lg">Gestionar Vídeos</div>
                <div className="text-white/80 text-sm">Añadir y editar contenido</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/knowledge')}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6 rounded-xl border-4 border-cyan-400 hover:scale-105 transition-all duration-300 shadow-xl group"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full">
                <FileText size={32} className="text-white" />
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-lg">Nueva Entrada Blog</div>
                <div className="text-white/80 text-sm">Crear contenido formativo</div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-gradient-to-r from-yellow-600 to-yellow-700 p-6 rounded-xl border-4 border-yellow-400 hover:scale-105 transition-all duration-300 shadow-xl group"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full">
                <Users size={32} className="text-white" />
              </div>
              <div className="text-left">
                <div className="text-white font-bold text-lg">Gestionar Usuarios</div>
                <div className="text-white/80 text-sm">Panel de administración</div>
              </div>
            </div>
          </button>
        </div>

        {/* Plan Stats */}
        <div className="bg-gray-900 rounded-xl p-6 border border-white/10">
          <h3 className="text-white text-xl font-bold mb-4 flex items-center gap-2">
            <Crown className="text-yellow-400" size={24} />
            Distribución de Planes
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 p-4 rounded-lg border border-yellow-500/30">
              <div className="text-yellow-400 text-sm mb-1">Profesional</div>
              <div className="text-white text-3xl font-bold">{stats.profesional}</div>
              <div className="text-gray-400 text-xs">€24.99/mes</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-4 rounded-lg border border-purple-500/30">
              <div className="text-purple-400 text-sm mb-1">Avanzado</div>
              <div className="text-white text-3xl font-bold">{stats.avanzado}</div>
              <div className="text-gray-400 text-xs">€4.99/mes</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-4 rounded-lg border border-blue-500/30">
              <div className="text-blue-400 text-sm mb-1">Usuario</div>
              <div className="text-white text-3xl font-bold">{stats.usuario}</div>
              <div className="text-gray-400 text-xs">GRATIS</div>
            </div>
            <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 p-4 rounded-lg border border-red-500/30">
              <div className="text-red-400 text-sm mb-1">Baneados</div>
              <div className="text-white text-3xl font-bold">{stats.banned}</div>
              <div className="text-gray-400 text-xs">Suspendidos</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
